/**
 * Quantos adesivos de `size` cabem em `available`, com `gap` entre eles.
 *
 * Nunca menos de um: um modelo com adesivo maior que a folha ainda imprime um
 * por página, cortado — que é o que o GDOOR faz, e aparece na prévia.
 */
export const fitCount = (available, size, gap) => size <= 0 ? 1 : Math.max(1, Math.floor((available + gap) / (size + gap)));
/**
 * O vão entre fileiras que espalha `rows` fileiras pela folha (F140).
 *
 * É como o GDOOR distribui uma folha de adesivos: a altura útil (entre a
 * margem de cima e a de baixo) dividida pelas `Linhas` do modelo é o passo de
 * uma fileira, e o que o passo tem além da altura do adesivo é o vão. No
 * `ETIQUETAS TONY 2024`: (297 − 5,5) ÷ 10 = 29,15 mm de passo, 24 de adesivo,
 * 5,15 de vão.
 *
 * Sem este vão o Aeris colava uma fileira na outra, de 24 em 24 mm, e a folha
 * de 40 selos terminava 51 mm antes do fim: cada fileira escorregava 5 mm para
 * cima do recorte, e a última ficava em branco.
 *
 * Nunca negativo: uma altura que não cabe nas fileiras pedidas (o `PLACA
 * BATERIA GAVETA` pede 5 de 75 mm) fica sem vão, e as fileiras que cabem saem.
 */
export const spreadRowGapMm = (measures) => {
    const rows = Math.max(1, Math.trunc(measures.rows));
    if (rows === 1)
        return 0;
    const usable = measures.pageHeightMm - measures.marginTopMm - measures.marginBottomMm;
    const gap = usable / rows - measures.labelHeightMm;
    return gap <= 0 ? 0 : Math.round(gap * 100) / 100;
};
export const sheetGeometry = (measures) => {
    const columns = Math.min(Math.max(1, Math.trunc(measures.columns)), fitCount(measures.pageWidthMm - measures.marginLeftMm, measures.labelWidthMm, measures.columnGapMm));
    /*
     * Bobina: a página é UMA fileira de adesivos, e a impressora avança o papel
     * de fileira em fileira. Uma página alta numa bobina faria o driver puxar
     * papel em branco até completar a "folha".
     */
    const isRoll = measures.paper === 'roll';
    const pageHeightMm = isRoll
        ? measures.marginTopMm + measures.labelHeightMm + measures.marginBottomMm
        : measures.pageHeightMm;
    const rowGapMm = isRoll ? 0 : Math.max(0, measures.rowGapMm);
    const rows = isRoll
        ? 1
        : Math.min(Math.max(1, Math.trunc(measures.rows)), fitCount(pageHeightMm - measures.marginTopMm, measures.labelHeightMm, rowGapMm));
    return {
        page: {
            widthMm: measures.pageWidthMm,
            heightMm: pageHeightMm,
            marginLeftMm: measures.marginLeftMm,
            marginTopMm: measures.marginTopMm,
            marginRightMm: measures.marginRightMm,
            marginBottomMm: measures.marginBottomMm,
        },
        label: {
            widthMm: measures.labelWidthMm,
            heightMm: measures.labelHeightMm,
        },
        columns,
        rows,
        columnGapMm: measures.columnGapMm,
        rowGapMm,
    };
};
/**
 * O caminho de volta: as medidas de um modelo gravado, para abrir no editor.
 *
 * Um modelo é bobina quando a página tem uma fileira só e é da altura do
 * adesivo; os seis da Elite são A4 e abrem como folha.
 */
export const measuresOf = (layout) => {
    const rollHeight = layout.page.marginTopMm + layout.label.heightMm + layout.page.marginBottomMm;
    return {
        paper: layout.rows === 1 && layout.page.heightMm <= rollHeight + 0.5
            ? 'roll'
            : 'sheet',
        pageWidthMm: layout.page.widthMm,
        pageHeightMm: layout.page.heightMm,
        marginLeftMm: layout.page.marginLeftMm,
        marginRightMm: layout.page.marginRightMm,
        marginTopMm: layout.page.marginTopMm,
        marginBottomMm: layout.page.marginBottomMm,
        labelWidthMm: layout.label.widthMm,
        labelHeightMm: layout.label.heightMm,
        columnGapMm: layout.columnGapMm,
        rowGapMm: layout.rowGapMm,
        columns: layout.columns,
        rows: layout.rows,
    };
};
/**
 * O modelo é de bobina? A mesma regra que abre o editor (F264).
 *
 * Quem pergunta é quem precisa dizer o papel ao driver: o Teste da bobina e a
 * dica de impressão. Uma regra só, para que a tela da fila e o editor nunca
 * discordem sobre o que é bobina.
 */
export const isRollLayout = (layout) => measuresOf(layout).paper === 'roll';
//# sourceMappingURL=sheet-geometry.js.map